import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Send, Plus, Loader2, Mic, Square, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { drApi, voiceApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useConsentStore } from "@/store/consentStore";

const TTS_KEY = "trata_tts_enabled";

// Map mobile deep-link routes to available web routes.
function mapRoute(route = "", label = "") {
  const r = (route + " " + label).toLowerCase();
  if (r.includes("emergency") || r.includes("sos") || r.includes("ambulance")) return "/emergency";
  if (r.includes("hospital") || r.includes("find-hospitals")) return "/find-care";
  if (r.includes("doctor")) return "/find-care";
  if (r.includes("record")) return "/records";
  if (r.includes("for-you") || r.includes("booking")) return "/for-you";
  if (r.includes("home")) return "/home";
  return null;
}

// Minimal markdown renderer: **bold**, ## headings, newlines.
function renderText(text) {
  return text.split("\n").map((line, i) => {
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      return (
        <span key={i} className="block font-semibold text-base mt-1 mb-0.5">
          {heading[2]}
        </span>
      );
    }
    const content = line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
      part.startsWith("**") && part.endsWith("**") ? <strong key={j}>{part.slice(2, -2)}</strong> : <span key={j}>{part}</span>
    );
    return (
      <span key={i} className="block">
        {content}
      </span>
    );
  });
}

export default function DrSanjeevani() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openFeatureGate = useConsentStore((s) => s.openFeatureGate);
  const scrollRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(true);

  // Voice state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(() => localStorage.getItem(TTS_KEY) === "1");
  const [speakingId, setSpeakingId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, sending]);

  // Stop any playing audio on unmount.
  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  const speak = useCallback(async (text, id) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (speakingId === id) {
      setSpeakingId(null);
      return;
    }
    try {
      setSpeakingId(id);
      const res = await voiceApi.tts(text.replace(/[#*]/g, ""), "en");
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setSpeakingId(null);
      await audio.play();
    } catch (e) {
      setSpeakingId(null);
      toast.error("Could not play audio.");
    }
  }, [speakingId]);

  const boot = useCallback(async () => {
    if (!user?._id) return;
    const granted = await openFeatureGate();
    if (!granted) {
      toast.info("Consent is required to use Dr Sanjeevani.");
      navigate("/home", { replace: true });
      return;
    }
    try {
      const hist = await drApi.history(user._id);
      const past = (hist.data?.messages || []).map((m, i) => ({ role: m.role, content: m.content, id: "h" + i }));
      if (past.length > 0) {
        setMessages(past);
      } else {
        const n = await drApi.nudges(user._id);
        setMessages([
          {
            role: "assistant",
            id: "greeting",
            content: n.data?.greeting || "Namaste! I'm Dr Sanjeevani. How are you feeling today?",
            quick_replies: n.data?.quick_replies || [],
          },
        ]);
      }
    } catch (e) {
      setMessages([{ role: "assistant", id: "greeting", content: "Namaste! I'm Dr Sanjeevani. How can I help you today?" }]);
    } finally {
      setBooting(false);
    }
  }, [user?._id, openFeatureGate, navigate]);

  useEffect(() => {
    boot();
  }, [boot]);

  // When navigated from a Health Record with ?discussReport=1, auto-ask about that report.
  const discussHandledRef = useRef(false);
  useEffect(() => {
    if (booting || discussHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("discussReport") !== "1") return;
    const ctx = localStorage.getItem("ds_report_context");
    const title = localStorage.getItem("ds_report_title") || "my report";
    localStorage.removeItem("ds_report_context");
    localStorage.removeItem("ds_report_title");
    if (!ctx) return;
    discussHandledRef.current = true;
    send(`Please explain my report "${title}" in simple terms and tell me what I should do next.\n\n${ctx}`);
  }, [booting]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: "u" + Date.now() }]);
    setSending(true);
    try {
      const res = await drApi.chat({ user_id: user._id, message: msg, language: "en" });
      const id = "a" + Date.now();
      const content = res.data?.response || "I'm here to help.";
      setMessages((m) => [
        ...m,
        { role: "assistant", id, content, quick_replies: res.data?.quick_replies || [], actions: res.data?.suggested_actions || [] },
      ]);
      if (ttsEnabled) speak(content, id);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", id: "e" + Date.now(), content: "Sorry, I couldn't reach the assistant. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  // ---- Voice recording (browser MediaRecorder → Whisper) ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) {
          toast.info("Recording too short. Hold and speak a little longer.");
          return;
        }
        setTranscribing(true);
        try {
          const res = await voiceApi.transcribe(blob, "");
          const text = (res.data?.text || "").trim();
          if (text) send(text);
          else toast.info("Didn't catch that. Please try again.");
        } catch (err) {
          toast.error("Could not transcribe. Please try again.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      if (e && (e.name === "NotAllowedError" || e.name === "SecurityError")) {
        toast.error("Microphone blocked. Enable mic access in your browser settings.");
      } else {
        toast.error("Could not access the microphone.");
      }
    }
  };
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };
  const toggleMic = () => (recording ? stopRecording() : startRecording());

  const toggleTts = () => {
    const next = !ttsEnabled;
    setTtsEnabled(next);
    localStorage.setItem(TTS_KEY, next ? "1" : "0");
    if (!next && audioRef.current) {
      audioRef.current.pause();
      setSpeakingId(null);
    }
  };

  const newChat = async () => {
    try {
      await drApi.newChat(user._id);
    } catch (e) {
      // ignore — starting fresh regardless
    }
    setMessages([]);
    setBooting(true);
    boot();
  };

  const onAction = (a) => {
    const to = mapRoute(a.route, a.label);
    if (to) navigate(to);
    else toast.info(`${a.label} is available on the Trata mobile app.`);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)] md:h-[calc(100vh-5rem)] -mb-20 md:mb-0">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-primary flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <p className="font-semibold leading-none">Dr Sanjeevani</p>
            <p className="text-xs text-muted-foreground mt-1">AI Health Assistant • Always online</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={toggleTts}
            data-testid="tts-toggle"
            title={ttsEnabled ? "Voice replies on" : "Voice replies off"}
          >
            {ttsEnabled ? <Volume2 className="h-5 w-5 text-primary" /> : <VolumeX className="h-5 w-5 text-muted-foreground" />}
          </Button>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={newChat} data-testid="new-chat-button">
            <Plus className="h-4 w-4" /> New
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-5 space-y-4 no-scrollbar" data-testid="chat-messages">
        {booting ? (
          <div className="flex justify-center pt-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={m.role === "user" ? "max-w-[85%]" : "max-w-[90%] w-full"}>
                <div
                  className={`px-4 py-3 rounded-3xl text-sm leading-relaxed ${
                    m.role === "user" ? "bg-primary text-primary-foreground rounded-br-lg" : "bg-card border border-border rounded-bl-lg"
                  }`}
                >
                  {renderText(m.content)}
                  {m.role === "assistant" && (
                    <button
                      onClick={() => speak(m.content, m.id || "m" + i)}
                      data-testid={`speak-${i}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                    >
                      {speakingId === (m.id || "m" + i) ? <Square className="h-3 w-3" /> : <Volume2 className="h-3.5 w-3.5" />}
                      {speakingId === (m.id || "m" + i) ? "Stop" : "Read aloud"}
                    </button>
                  )}
                </div>
                {m.role === "assistant" && m.quick_replies?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {m.quick_replies.map((q, qi) => (
                      <button
                        key={qi}
                        onClick={() => send(q)}
                        data-testid={`quick-reply-${qi}`}
                        className="text-sm px-3 h-8 rounded-full border border-primary/30 text-primary hover:bg-accent transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
                {m.role === "assistant" && m.actions?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {m.actions.map((a, ai) => (
                      <button
                        key={ai}
                        onClick={() => onAction(a)}
                        data-testid={`cta-${ai}`}
                        className="text-sm px-3 h-8 rounded-full bg-secondary text-secondary-foreground hover:opacity-90 transition-opacity"
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {(sending || transcribing) && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-3xl rounded-bl-lg bg-card border border-border flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {transcribing ? "Transcribing..." : ""}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="pt-3 border-t border-border bg-background">
        <div className="flex items-center gap-2 rounded-3xl border border-input px-2 pl-4 h-14 focus-within:ring-1 focus-within:ring-ring bg-card">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={recording ? "Listening... tap stop when done" : "Ask Dr Sanjeevani anything..."}
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="chat-input"
            disabled={recording}
          />
          <Button
            size="icon"
            variant={recording ? "destructive" : "ghost"}
            className="rounded-2xl h-10 w-10 shrink-0"
            onClick={toggleMic}
            disabled={transcribing || sending}
            data-testid="mic-button"
            title={recording ? "Stop recording" : "Speak"}
          >
            {recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
          </Button>
          <Button size="icon" className="rounded-2xl h-10 w-10 shrink-0" onClick={() => send()} disabled={sending || recording || !input.trim()} data-testid="send-button">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center mt-2">
          Dr Sanjeevani may make mistakes. For emergencies, call 108.
        </p>
      </div>
    </div>
  );
}
